import React, { useEffect, useRef, useState } from 'react';
import { useEditor, DEFAULT_TEXT_PROPS } from '../../context/EditorContext';
import { isTextType, isShapeWithText } from '../../lib/selection';
import { shapeTextObject } from '../../lib/shapeText';
import { padsOf } from '../../lib/textWrap';
import { getBaselineGuide, BASELINE_GUIDE_ID, TAB_ALIGNMENTS } from '../../lib/textLayout';
import { rulerSnapStep } from '../../lib/guideSnap';

/**
 * Publisher-style ruler markers.
 *  - Horizontal ruler, while a text box (or a shape holding text) is selected: the text area is painted white, the
 *    paragraph indents show as draggable triangles, custom tab stops as draggable glyphs; clicking in the text area
 *    sets a new stop of the type shown by the selector at the ruler's left end (Left → Center → Right → Decimal);
 *    dragging a stop off the ruler removes it; double-click opens Paragraph ▸ Tabs.
 *  - Vertical ruler, while View ▸ Baselines is on: one marker per baseline guide; dragging the first marker moves the
 *    grid (offset), dragging any other marker changes the spacing so that line lands where it is dropped;
 *    double-click opens Layout Guides ▸ Baseline Guides.
 */

export const RULER_SIZE = 24;
const TAB_TYPES = ['left', 'center', 'right', 'decimal'];
const TAB_LABEL = Object.fromEntries(TAB_ALIGNMENTS);
const TAB_TYPE_PREF = 'wp.rulerTabType';

/** Minor ruler tick in canvas px — markers (and ruler guides) snap to it unless Alt is held (1/8 in, 1 mm, 6 pt, 10 px). */
export { rulerSnapStep };

// Ruler interactions never end text editing (the ruler is data-keep-editing) and never steal the caret: the mousedown's
// default (focus change) is cancelled, so a tab stop set while typing applies to the box being edited straight away.
const stopMouse = (e) => { e.stopPropagation(); e.preventDefault(); };

/** The text frame whose tabs / indents the horizontal ruler shows: a single selected, unrotated, horizontal text target. */
export const useRulerTextTarget = () => {
  const { state, getSelectedObjects } = useEditor();
  const sel = getSelectedObjects();
  const o = sel.length === 1 ? sel[0] : null;
  if (!o) return null;
  if (!(isTextType(o) || isShapeWithText(o, state.editingObjectId))) return null;
  if ((((o.rotation || 0) % 360) + 360) % 360 !== 0) return null;
  const isShape = o.type === 'shape';
  const props = isShape ? shapeTextObject(o).properties : { ...DEFAULT_TEXT_PROPS, ...(o.properties || {}) };
  if (props.textDirection === 'vertical') return null;
  const pads = padsOf(props);
  return { obj: o, props, isShape, contentLeft: o.x + pads.l, contentRight: o.x + o.width - pads.r, locked: !!o.locked };
};

/** Tab-stop glyph as drawn on Office rulers: ⌊ left, ⊥ centre, ⌋ right, ⊥• decimal. */
export const TabTypeGlyph = ({ type = 'left', size = 12, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" className={className} aria-hidden="true">
    {type === 'left' && <path d="M2 1.5 V10 H10.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="square" />}
    {type === 'right' && <path d="M10 1.5 V10 H1.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="square" />}
    {(type === 'center' || type === 'decimal') && <path d="M6 1.5 V10 M1.5 10 H10.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="square" />}
    {type === 'decimal' && <circle cx="9.6" cy="3.6" r="1.5" fill="currentColor" />}
  </svg>
);

const Triangle = ({ dir }) => (
  // dir 'down' = first-line indent (hangs from the top), 'up' = left / right indent (stands on the bottom edge)
  <svg width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
    <path d={dir === 'down' ? 'M0.5 0.5 H11.5 L6 7.5 Z' : 'M0.5 7.5 H11.5 L6 0.5 Z'} fill="hsl(var(--primary))" stroke="hsl(var(--primary-foreground))" strokeWidth="0.8" />
  </svg>
);

/** White band over the selected text frame's text area (rendered underneath the tick marks). */
export const TextRulerBand = ({ target, zoom, toScreen }) => {
  if (!target) return null;
  const left = toScreen(target.contentLeft);
  const width = Math.max(2, (target.contentRight - target.contentLeft) * zoom);
  return <div className="absolute top-0 bottom-0 bg-background dark:bg-foreground/15 border-x border-border/60 dark:border-foreground/30" style={{ left, width }} data-testid="ruler-text-band" />;
};

/** Interactive layer of the horizontal ruler: tab-type selector, indent markers, tab stops, click-to-set. */
export const TextRulerMarkers = ({ target, zoom, toScreen, toCanvas, rulerRef, unit }) => {
  const { dispatch, openDialog, pxToUnit } = useEditor();
  const [tabType, setTabType] = useState(() => { try { const v = window.localStorage.getItem(TAB_TYPE_PREF); return TAB_TYPES.includes(v) ? v : 'left'; } catch { return 'left'; } });
  const [drag, setDrag] = useState(null); // { kind: 'tab' | 'indentLeft' | 'firstLine' | 'indentRight', index, startStops, removing }
  const dragging = !!drag;
  const dragRef = useRef(null);
  const latest = useRef(null);

  const { obj, props, contentLeft, contentRight, locked } = target || {};
  const contentW = target ? Math.max(1, contentRight - contentLeft) : 1;
  const stops = target ? (props.tabStops || []).slice().sort((a, b) => a.pos - b.pos) : [];
  const indentLeft = target ? props.indentLeft || 0 : 0;
  const indentRight = target ? props.indentRight || 0 : 0;
  const firstLine = target ? props.firstLineIndent || 0 : 0;
  const step = rulerSnapStep(unit);
  latest.current = { obj, contentLeft, contentW, stops, indentLeft, indentRight, firstLine, step, toCanvas, rulerRef, dispatch, tabType };

  const cycleTabType = () => {
    const n = TAB_TYPES[(TAB_TYPES.indexOf(tabType) + 1) % TAB_TYPES.length];
    setTabType(n);
    try { window.localStorage.setItem(TAB_TYPE_PREF, n); } catch { /* storage unavailable */ }
  };

  // Window-level drag handling (markers re-sort while dragging, so pointer capture on the element is not reliable)
  useEffect(() => {
    if (!dragging) return undefined;
    const L = latest;
    const update = (p) => L.current.dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [L.current.obj.id], props: p } });
    const snap = (v, alt) => (alt ? v : Math.round(v / L.current.step) * L.current.step);
    const contentX = (clientX) => { const r = L.current.rulerRef.current.getBoundingClientRect(); return L.current.toCanvas(clientX - r.left) - L.current.contentLeft; };
    const apply = (e, commit) => {
      const d = dragRef.current;
      if (!d) return;
      const { contentW: cw, indentLeft: il, indentRight: ir } = L.current;
      const x = contentX(e.clientX);
      if (d.kind === 'tab') {
        const r = L.current.rulerRef.current.getBoundingClientRect();
        const removing = e.clientY < r.top - 14 || e.clientY > r.bottom + 14;
        const pos = Math.round(Math.max(1, Math.min(cw, snap(x, e.altKey))));
        let next = d.startStops.map((s, i) => (i === d.index ? { ...s, pos } : s)).filter((s, i) => i === d.index || Math.abs(s.pos - pos) > 0.5);
        if (commit && removing) next = d.startStops.filter((_, i) => i !== d.index);
        update({ tabStops: next.sort((a, b) => a.pos - b.pos) });
        dragRef.current = { ...d, pos, removing };
        setDrag(dragRef.current);
      } else if (d.kind === 'indentLeft') {
        update({ indentLeft: Math.round(Math.max(0, Math.min(cw - ir - 8, snap(x, e.altKey)))) });
      } else if (d.kind === 'firstLine') {
        update({ firstLineIndent: Math.round(Math.max(-il, Math.min(cw - ir - il - 8, snap(x, e.altKey) - il))) });
      } else if (d.kind === 'indentRight') {
        update({ indentRight: Math.round(Math.max(0, Math.min(cw - il - 8, snap(cw - x, e.altKey)))) });
      }
    };
    const onMove = (e) => apply(e, false);
    const onUp = (e) => { apply(e, true); dragRef.current = null; setDrag(null); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = 'ew-resize';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = prevCursor;
    };
  }, [dragging]);

  if (!target) return null;

  const update = (p) => dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [obj.id], props: p } });
  const beginDrag = (e, d) => {
    if (locked || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { ...d, startStops: d.startStops || stops };
    setDrag(dragRef.current);
  };
  const contentX = (clientX) => { const r = rulerRef.current.getBoundingClientRect(); return toCanvas(clientX - r.left) - contentLeft; };
  const snapV = (v, alt) => (alt ? v : Math.round(v / step) * step);

  /** Click in the text area: set a stop of the current type there and start dragging it. */
  const onBandPointerDown = (e) => {
    if (locked || e.button !== 0) return;
    const pos = Math.round(Math.max(1, Math.min(contentW, snapV(contentX(e.clientX), e.altKey))));
    const next = [...stops.filter((s) => Math.abs(s.pos - pos) > 0.5), { pos, align: tabType, leader: 'none' }].sort((a, b) => a.pos - b.pos);
    update({ tabStops: next });
    beginDrag(e, { kind: 'tab', index: next.findIndex((s) => s.pos === pos), pos, startStops: next });
  };
  const onTabKeyDown = (e, i) => {
    const s = stops[i];
    const delta = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (delta) {
      e.preventDefault();
      const pos = Math.round(Math.max(1, Math.min(contentW, s.pos + delta * (e.shiftKey ? step : 1))));
      update({ tabStops: stops.map((t, k) => (k === i ? { ...t, pos } : t)).filter((t, k) => k === i || Math.abs(t.pos - pos) > 0.5).sort((a, b) => a.pos - b.pos) });
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      update({ tabStops: stops.filter((_, k) => k !== i) });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      openDialog('paragraph', { tab: 'tabs' });
    }
  };
  const fmt = (px) => `${pxToUnit(px)} ${unit}`;
  const bandLeft = toScreen(contentLeft);
  const bandW = Math.max(2, contentW * zoom);

  const indentMarker = (kind, canvasX, dir, top, label, testId) => (
    <div
      role="button"
      tabIndex={locked ? -1 : 0}
      aria-label={label}
      title={label}
      className={`absolute w-3 h-2 ${locked ? 'opacity-50' : 'cursor-ew-resize'}`}
      style={{ left: toScreen(canvasX) - 6, top, pointerEvents: 'auto' }}
      onMouseDown={stopMouse}
      onPointerDown={(e) => beginDrag(e, { kind })}
      onDoubleClick={(e) => { e.stopPropagation(); openDialog('paragraph', { tab: 'indents' }); }}
      data-testid={testId}
    >
      <Triangle dir={dir} />
    </div>
  );

  return (
    <>
      {/* text area: click sets a tab stop of the selected type */}
      <div
        className={`absolute top-0 bottom-0 ${locked ? '' : 'cursor-pointer'}`}
        style={{ left: bandLeft, width: bandW, pointerEvents: 'auto' }}
        title={locked ? 'The object is locked' : `Click to set a ${TAB_LABEL[tabType].toLowerCase()} tab stop (Alt = no snapping)`}
        onMouseDown={stopMouse}
        onPointerDown={onBandPointerDown}
        data-testid="ruler-tab-area"
      />
      {/* tab stops */}
      {stops.map((s, i) => {
        const dragging = drag?.kind === 'tab' && drag.pos === s.pos;
        const label = `${TAB_LABEL[s.align] || 'Left'} tab stop at ${fmt(s.pos)}${s.leader && s.leader !== 'none' ? `, ${s.leader} leader` : ''}`;
        return (
          <div
            key={i}
            role="button"
            tabIndex={locked ? -1 : 0}
            aria-label={label}
            title={dragging && drag.removing ? 'Release to remove the tab stop' : `${label} — drag to move, drag off the ruler to remove, double-click for Tabs…`}
            className={`absolute w-3 h-3 flex items-center justify-center text-foreground ${locked ? 'opacity-50' : 'cursor-ew-resize hover:text-primary'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm`}
            style={{ left: toScreen(contentLeft + s.pos) - 6, top: 6, opacity: dragging && drag.removing ? 0.35 : undefined, pointerEvents: 'auto' }}
            onMouseDown={stopMouse}
            onPointerDown={(e) => beginDrag(e, { kind: 'tab', index: i, pos: s.pos })}
            onDoubleClick={(e) => { e.stopPropagation(); openDialog('paragraph', { tab: 'tabs' }); }}
            onKeyDown={(e) => onTabKeyDown(e, i)}
            data-testid={`ruler-tab-stop-${i}`}
            data-pos={s.pos}
            data-align={s.align}
          >
            <TabTypeGlyph type={s.align} size={12} />
          </div>
        );
      })}
      {/* paragraph indents */}
      {indentMarker('firstLine', contentLeft + indentLeft + firstLine, 'down', 0, `First-line indent ${fmt(firstLine)} — drag to change`, 'ruler-indent-first')}
      {indentMarker('indentLeft', contentLeft + indentLeft, 'up', RULER_SIZE - 9, `Left indent ${fmt(indentLeft)} — drag to change`, 'ruler-indent-left')}
      {indentMarker('indentRight', contentRight - indentRight, 'up', RULER_SIZE - 9, `Right indent ${fmt(indentRight)} — drag to change`, 'ruler-indent-right')}
      {/* tab type selector (Publisher: left end of the ruler) */}
      <button
        type="button"
        className="absolute left-0 top-0 h-full w-5 border-r border-border bg-muted text-foreground flex items-center justify-center hover:bg-[hsl(var(--wp-command-hover))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors duration-150"
        style={{ pointerEvents: 'auto' }}
        title={`Tab type: ${TAB_LABEL[tabType]} — click to change (Left → Center → Right → Decimal)`}
        aria-label={`Tab type: ${TAB_LABEL[tabType]}. Click to change.`}
        onMouseDown={stopMouse}
        onClick={cycleTabType}
        data-testid="ruler-tab-type"
        data-tab-type={tabType}
      >
        <TabTypeGlyph type={tabType} size={12} />
      </button>
    </>
  );
};

/** Vertical ruler: draggable baseline-guide markers (View ▸ Baselines). */
export const BaselineRulerMarkers = ({ toScreen, toCanvas, rulerRef, pageHeight, zoom }) => {
  const { state, rawDispatch, openDialog, pxToUnit } = useEditor();
  const [drag, setDrag] = useState(null); // { n }
  const dragging = !!drag;
  const dragRef = useRef(null);
  const latest = useRef(null);
  const vo = state.viewOptions || {};
  const grid = getBaselineGuide(state.guides, vo.baselineSpacing);
  latest.current = { grid, guides: state.guides, toCanvas, rulerRef, rawDispatch };

  useEffect(() => {
    if (!dragging) return undefined;
    const L = latest;
    const setGrid = (spacing, offset) => {
      const s = Math.max(2, Math.round(spacing * 2) / 2);
      const off = Math.round(((offset % s) + s) % s);
      const exists = L.current.guides.some((g) => g && (g.id === BASELINE_GUIDE_ID || g.kind === 'baseline'));
      if (exists) L.current.rawDispatch({ type: 'UPDATE_GUIDE', payload: { id: BASELINE_GUIDE_ID, data: { kind: 'baseline', spacing: s, offset: off } } });
      else L.current.rawDispatch({ type: 'ADD_GUIDE', payload: { id: BASELINE_GUIDE_ID, kind: 'baseline', spacing: s, offset: off } });
      L.current.rawDispatch({ type: 'SET_VIEW_OPTION', payload: { key: 'baselineSpacing', value: s } });
    };
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const r = L.current.rulerRef.current.getBoundingClientRect();
      const y = L.current.toCanvas(e.clientY - r.top);
      const { spacing, offset } = L.current.grid;
      if (d.n === 0) setGrid(spacing, Math.max(0, y));
      else setGrid(Math.max(2, (y - offset) / d.n), offset);
    };
    const onUp = (e) => { onMove(e); dragRef.current = null; setDrag(null); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = 'ns-resize';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = prevCursor;
    };
  }, [dragging]);

  if (!vo.baselines) return null;
  const { spacing, offset } = grid;
  const count = Math.max(0, Math.floor((pageHeight - offset) / spacing)) + 1;
  // Keep the DOM light when zoomed far out: skip markers that would sit on top of each other
  const every = Math.max(1, Math.ceil(5 / (spacing * zoom)));
  const markers = [];
  for (let n = 0; n < count; n += n === 0 ? 1 : every) markers.push(n);
  const beginDrag = (e, n) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { n };
    setDrag(dragRef.current);
  };
  const pt = (px) => `${(px * 0.75).toFixed(1)} pt`;
  return (
    <>
      {markers.map((n) => {
        const y = offset + n * spacing;
        const sy = toScreen(y);
        const first = n === 0;
        const label = first
          ? `Baseline offset ${pt(offset)} (${pxToUnit(offset)} ${state.canvasUnit}) — drag to move the grid, double-click for Baseline Guides…`
          : `Baseline ${n + 1} at ${pxToUnit(y)} ${state.canvasUnit} — drag to change the spacing (now ${pt(spacing)})`;
        return (
          <div
            key={n}
            role="button"
            tabIndex={first || n === 1 ? 0 : -1}
            aria-label={label}
            title={label}
            className={`absolute flex items-center justify-end cursor-ns-resize ${drag?.n === n ? 'text-primary' : 'text-sky-500 hover:text-primary'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
            style={{ top: sy - 3, left: first ? 6 : 12, width: first ? 18 : 12, height: 7, pointerEvents: 'auto' }}
            onMouseDown={stopMouse}
            onPointerDown={(e) => beginDrag(e, n)}
            onDoubleClick={(e) => { e.stopPropagation(); openDialog('layout-guides', { tab: 'baseline' }); }}
            data-testid={first ? 'ruler-baseline-offset' : `ruler-baseline-${n}`}
            data-y={y}
          >
            {first ? (
              <svg width="18" height="7" viewBox="0 0 18 7" aria-hidden="true"><path d="M0.5 0.5 L6.5 3.5 L0.5 6.5 Z" fill="currentColor" /><line x1="6" y1="3.5" x2="18" y2="3.5" stroke="currentColor" strokeWidth="1.5" /></svg>
            ) : (
              <svg width="12" height="7" viewBox="0 0 12 7" aria-hidden="true"><line x1="0" y1="3.5" x2="12" y2="3.5" stroke="currentColor" strokeWidth="1.2" /></svg>
            )}
          </div>
        );
      })}
    </>
  );
};
